import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { isNil } from 'lodash-es';

import { ProductCode } from '@dev/ee-api/ee-api-models';
import { ABCampaignIcon, PrimaryButton, palette, Typography } from '@dev/ee-components';
import { generateIdBasedOnPath, safelyEncodeBase64Url } from '@dev/ee-tools';

import styled from 'styled-components';

import { LockIcon } from '@/services/icons/LockedIcon';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { BillingPaths, CampaignCreatorType, CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { getCampaignsStore, SetCampaignCreatorOpen } from '@/store/campaigns/actions';

const SelectCampaignWrapper = styled.div`
    background: #fff;
    border: 1px solid ${palette.gray[3]};
    margin-right: 30px;
    max-height: 440px;
`;

const CampaignSelectDescribe = styled.div`
    display: flex;
    flex-direction: column;
    padding: 40px;
    border-top: 1px solid ${palette.gray[3]};
    align-items: center;
`;

export const ABCampaign = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { contactsList, templateName } = useSelector(getCampaignsStore);
    const { isPilotPlan, productCode } = useCurrentPlanStatus();

    const clickABButton = (): void => {
        if (isNil(contactsList) || isNil(templateName)) {
            dispatch(Push(CampaignsPaths.CampaignCreator.replace(pathsParams.type, CampaignCreatorType.AB)));
        }
        if (!isNil(contactsList)) {
            dispatch(
                Push(
                    CampaignsPaths.CampaignCreatorTarget.replace(pathsParams.type, CampaignCreatorType.AB).replace(
                        pathsParams.listName,
                        safelyEncodeBase64Url(contactsList)
                    )
                )
            );
        }
        if (!isNil(templateName)) {
            dispatch(
                Push(
                    CampaignsPaths.CampaignCreatorTemplate.replace(pathsParams.type, CampaignCreatorType.AB).replace(
                        pathsParams.template,
                        safelyEncodeBase64Url(templateName)
                    )
                )
            );
        }
        dispatch(SetCampaignCreatorOpen(false));
    };

    const clickUpgradeBtn = (): void => {
        window.location.href = `${location.origin}${BillingPaths.BillingPlanList.replace(
            pathsParams.productCode,
            (productCode === ProductCode.CreatorSuite ? ProductCode.CreatorSuite : ProductCode.Marketing).toLowerCase()
        )}`;
    }

    return (
        <SelectCampaignWrapper>
            <ABCampaignIcon />
            <CampaignSelectDescribe>
                <Typography variant="h2">{t('abtest')}</Typography>
                <Typography variant="body1" bodyVariant="onPaper" align="center" style={{ margin: '20px 0 32px' }}>
                    {t('abcampaign_desc')}
                </Typography>
                {isPilotPlan ? <PrimaryButton
                        id={generateIdBasedOnPath('upgrade-to-unlock-button')}
                        text={t('upgradetounlock')}
                        startIcon={<LockIcon width={20} height={20} />}
                        onClick={clickUpgradeBtn}
                    /> :
                    <PrimaryButton
                        id={generateIdBasedOnPath('create-ab-campaign-button')}
                        text={t('create')}
                        onClick={clickABButton}
                    />
                }
            </CampaignSelectDescribe>
        </SelectCampaignWrapper>
    );
};
